import { Vector3, SphereGeometry, Mesh, Quaternion, Group } from "three";
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer";
import { loading } from "../loading/loading";
import { sceneManager } from "../script";
import { normalizeAlphabetic, setLabelVisible } from "../helpers.js";
import { bodies } from "../configEntities/celestialDynamicsConfig.js";
import { createMaterial } from "./materialFactory.js";
import { cameraLabelClick } from "../utils/cameraUtils.js";
import { createOrbitCircle, orbitWarp, orbit } from "../utils/orbitUtils.js";
import { INCLIN_GALAXY, WAVE_SPEED } from "../configEntities/galaxyConfig.js";
import { updateJets } from '../utils/jets.js';

export class Entity {
  constructor(
    centerElement,
    size,
    texture,
    orbitRadius,
    axialTilt,
    orbitInclin,
    speedTrans,
    speedRot,
    eccentricity,
    argPeriapsis,
    longAscNode,
    label
  ) {
    this.centerElement = centerElement;
    this.center = new Vector3(
      centerElement.mesh.position.x,
      centerElement.mesh.position.y,
      centerElement.mesh.position.z
    );
    this.time = 0;
    this.precessOrbitPhase = 0;
    this.precessRotPhase = 0;
    this.rotPhase = 0;
    this.warpPhase = 0;
    this.size = size;
    this.texture = texture;
    this.orbitRadius = orbitRadius;
    this.axialTilt = axialTilt;
    this.orbitInclin = orbitInclin;
    this.speedTrans = speedTrans;
    this.eccentricity = eccentricity;
    this.name = label;
    this.argPeriapsis = argPeriapsis;
    this.longAscNode = longAscNode;
    let labeNorm = normalizeAlphabetic(this.name);
    this.objOsc = bodies[labeNorm];
    this.distScale = this.objOsc.distScale;
    this.distRot = this.objOsc.distRot;
    this.ia = this.objOsc.ia;
    this.ifreq = this.objOsc.ifreq;
    this.ra = this.objOsc.ra;
    this.rfreq = this.objOsc.rfreq;
    this.precession = this.objOsc.precession;
    this.haveMoon = this.objOsc.haveMoon;
    this.distShadow = this.objOsc.distShadow;
    this.precesseRot = this.objOsc.precessRot;
    this.alignToEquator = this.objOsc.precessAlignToEq ?? false;

    this.textureLoader = loading.textureLoader;
    this.geo = new SphereGeometry(this.size, 64, 64);
    this.geo.computeTangents();
    this.speedRot = speedRot;

    this.mat = createMaterial(labeNorm, this.textureLoader, this.texture, this);

    if (!this.mat) {
      this.mesh = new Group();
    } else {
      this.mesh = new Mesh(this.geo, this.mat);
    }
    if (this.meshAtm) {
      this.mesh.add(this.meshAtm);
    }

    this.mesh.position.x = this.orbitRadius;
    this.tiltRad = (this.axialTilt * Math.PI) / 180.0;

    const TILT_THRESHOLD = Math.PI / 4;

    this.precessionAxis = (
      this.tiltRad > TILT_THRESHOLD
        ? new Vector3(1, 0, 0)
        : new Vector3(0, 1, 0)
    ).normalize();

    const arbitrary =
      Math.abs(this.precessionAxis.dot(new Vector3(0, 1, 0))) < 0.9
        ? new Vector3(0, 1, 0)
        : new Vector3(1, 0, 0);

    const remTilt = this.tiltRad % (Math.PI / 2);

    const tiltPerp = this.precessionAxis.clone().cross(arbitrary).normalize();
    this.initialTiltAxis = this.precessionAxis
      .clone()
      .applyAxisAngle(tiltPerp, remTilt)
      .normalize();

    let directionTrail = Math.sign(this.orbitRadius);
    this.orbit = createOrbitCircle(
      this.orbitRadius,
      this.orbitInclin,
      this.ia,
      this.ifreq,
      this.ra,
      this.rfreq,
      this.argPeriapsis,
      this.eccentricity,
      directionTrail
    );
    if (this.objOsc.warp) {
      this.orbit.rotation.x = INCLIN_GALAXY;
    }
    if (this.objOsc.magnetic) {
      this.inclinMag = this.objOsc.magnetic.inclinMag;
      this.totalPhi = this.objOsc.magnetic.totalPhi;
      this.totalRings = this.objOsc.magnetic.totalRings;
      this.desiredMaxRmax = this.objOsc.magnetic.desiredMaxRmax;
      this.desiredMinRmax = this.objOsc.magnetic.desiredMinRmax;
      this.lateralTwist = this.objOsc.magnetic.lateralTwist;
      this.twistFrequency = this.objOsc.magnetic.twistFrequency;
      this.amortMag = this.objOsc.magnetic.amortMag;
      this.distMag = this.objOsc.magnetic.distMag;
    }
    sceneManager.scene.add(this.orbit);
    sceneManager.scene.add(this.mesh);
    this.div = document.createElement("div");
    this.div.className = "label";
    this.div.textContent = this.name;
    this.div.style.backgroundColor = "transparent";
    this.div.style.cursor = "pointer";
    this.div.addEventListener("click", () => {
      cameraLabelClick(labeNorm, this);
    });

    this.infoIcon = document.createElement("i");
    this.infoIcon.className = "fas fa-info-circle info-icon";
    this.infoIcon.style.pointerEvents = "auto";
    this.infoIcon.title = "More Information";


     this.div.appendChild(this.infoIcon);

    const canvas = sceneManager.renderer.domElement;
    this.div.addEventListener("wheel", (e) => {
      e.preventDefault();
      const evt = new WheelEvent("wheel", e);
      canvas.dispatchEvent(evt);
    });

    this.label = new CSS2DObject(this.div);
    this.label.center.set(0.15, 0.5);
    sceneManager.scene.add(this.label);
  }

  centerOrbit() {
    this.center = this.centerElement.mesh.position;
    this.orbit.position.copy(this.centerElement.mesh.position);
  }

  animate(speed) {
    this.time = (this.time + speed * this.speedTrans) % (2 * Math.PI);
    this.precessOrbitPhase =
      (this.precessOrbitPhase + speed * this.precession) % (2 * Math.PI);
    this.warpPhase = (this.warpPhase + speed * WAVE_SPEED) % (2 * Math.PI);
    this.rotPhase = (this.rotPhase + speed * this.speedRot) % (2 * Math.PI);
    this.precessRotPhase =
      (this.precessRotPhase + speed * this.precesseRot) % (2 * Math.PI);
    this.centerOrbit();
    this.orbit.visible = sceneManager.commands.controls.orbit;

    const dynamicAxis = this.centerElement?.dynamicAxis ?? new Vector3(0, 1, 0);
    const prePhase = this.centerElement?.precessRotPhase ?? 0;

    this.posCurrent = orbit({
      radius: this.orbitRadius,
      angle: this.orbitInclin,
      eccentricity: this.eccentricity,
      time: this.time,
      center: this.center,
      inclinationOsc: { amplitude: this.ia, frequency: this.ifreq },
      radiusOsc: { amplitude: this.ra, frequency: this.rfreq },
      orientation: {
        argPeriapsis: this.argPeriapsis,
        longAscNode: this.longAscNode,
        precessionPhase: this.precessOrbitPhase,
      },
      warp: {
        enabled: this.objOsc.warp,
        phase: this.warpPhase,
      },
      dynamicAxis: dynamicAxis,
      precessRotPhase: prePhase,
      alignToEquator: this.alignToEquator,
    });
    this.orbit.material.uniforms.uInvertAlpha.value = sceneManager.dimension;
    this.mesh.position.set(this.posCurrent.x0, this.posCurrent.y0, this.posCurrent.z0);

    if (this.jets) {
      this.mat.uniforms.uPerlin.value = sceneManager.cubeRenderTarget1.texture;
      this.mat.uniforms.time.value += 1e-3;
      this.nebula.animate(speed);
      this.jets.emitters.forEach((em) => (em.visible = sceneManager.commands.controls.spectrum));
      updateJets(this.jets);
    }
    this.label.visible = setLabelVisible(
      this.mesh,
      this.size,
      this.orbitRadius,
      this.label,
      sceneManager.camera,
      sceneManager.commands.controls.labels
    );
    orbitWarp(
      this.orbit,
      this.time,
      this.objOsc.warp,
      this.precessOrbitPhase,
      this.warpPhase,
      this.longAscNode,
      dynamicAxis,
      prePhase,
      this.alignToEquator
    );

    this.dynamicAxis = this.initialTiltAxis
      .clone()
      .applyAxisAngle(this.precessionAxis, this.precessRotPhase)
      .normalize();

    this.mesh.quaternion.setFromUnitVectors(new Vector3(0, 1, 0), this.dynamicAxis);

    this.mesh.rotateY(this.rotPhase);

    if (this.pivot) {
      this.pivot.position.copy(this.mesh.position);
      this.pivot.quaternion.setFromUnitVectors(new Vector3(0, 1, 0), this.dynamicAxis);

      this.pivot.rotateY(this.time + this.precessOrbitPhase);
    }
    this.orbit.geometry.attributes.position.needsUpdate = true;
    if (this.tail) {
      this.tail.animate();
    }
  }
}

